/* eslint-disable max-lines */
import Tippy from '@tippyjs/react';
import {
  appointmentBalanceState,
  appointmentCCState,
  appointmentCheckinState,
  claimState,
  clinicalNoteStatus,
  superbillStatus
} from 'constants';
import { capitalize } from 'lodash';
import moment from 'moment';

import { requestApi } from 'api/Api';
import {
  cancelMultipleAppointments,
  creationActionAppointmentMultiple,
  remindAppointmentMultiple
} from 'api/Appointment';

import {
  findAppointmentStatusColor,
  findAppointmentStatusLabel,
  formatDate,
  formatDateAndTime,
  formatPhoneNumber,
  mString
} from 'lib/helpers/utility';

import { showAlert } from 'components/shared/Alert/Alert';
import {
  APPOINTMENT_RESOURCE_OPTIONS,
  APPOINTMENT_TAG_OPTIONS,
  PATIENT_OPTIONS,
  PROVIDER_OPTIONS
} from 'components/shared/Filters/lib/filterOptions';
import Status from 'components/shared/Status/Status';

import Actions from '../components/Actions';

const appointmentConfirmState = [
  {
    value: true,
    label: 'Confirmed'
  },
  {
    value: false,
    label: 'Unconfirmed'
  },
  {
    value: 'cancelled',
    label: 'Canceled'
  }
];

export const defaultColumns = (timezone, appointmentStatuses) => [
  {
    field: 'id',
    headerName: 'ID',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? capitalize(value) : '-'),
    sortable: false,
    headerCheckboxSelection: true,
    checkboxSelection: (params) => !params.node.group
  },
  {
    field: 'starts_at',
    headerName: 'Date',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? formatDateAndTime(value, timezone) : '-'),
    sort: 'asc'
  },
  {
    field: 'patient.fullName',
    headerName: 'Patient',
    cellClass: 'no-border',
    sortable: false
  },
  {
    field: 'practitioner.fullName',
    headerName: 'Provider',
    cellClass: 'no-border',
    sortable: false
  },
  {
    field: 'patient.email',
    headerName: 'Patient Email',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? value : '-'),
    sortable: false
  },
  {
    field: 'patient.phone',
    headerName: 'Patient Phone',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (formatPhoneNumber(value) ? value : '-'),
    sortable: false
  },
  {
    field: 'invoice.procedures_label',
    headerName: 'Services',
    cellClass: 'no-border',
    sortable: false
  },
  {
    field: 'invoice.balance',
    headerName: 'Balance',
    cellClass: 'no-border',
    valueFormatter: ({ value }) => (value ? mString(value) : '-'),
    sortable: false
  },
  {
    field: 'status',
    headerName: 'Status',
    cellClass: 'no-border',
    cellRenderer: ({ value }) => (
      <Tippy
        arrow={true}
        placement="left-start"
        className="tippy-black w-fit"
        content={findAppointmentStatusLabel(appointmentStatuses, value)}>
        <div className="flex h-full items-center">
          <Status
            status={findAppointmentStatusLabel(appointmentStatuses, value)}
            color={findAppointmentStatusColor(appointmentStatuses, value)}
            className="flex min-w-[80px] items-center"
          />
        </div>
      </Tippy>
    ),
    maxWidth: 200,
    minWidth: 50,
    resizable: true,
    sortable: false
  },
  {
    field: 'actions',
    headerName: 'Actions',
    cellClass: 'no-border',
    cellRenderer: Actions,
    maxWidth: 88,
    minWidth: 36,
    resizable: false,
    sortable: false,
    pinned: 'right'
  }
];

export const DEFAULT_FILTERS = {
  appointment_date: {
    values: {
      startDate: formatDate(moment().subtract(2, 'month').toDate()),
      id: 'custom-date-range',
      endDate: formatDate(moment().add(1, 'week').toDate())
    },
    formula: 'is',
    type: 'date-range',
    title: 'Appointment Date',
    default: true,
    icon: 'calendar-mono'
  },
  provider: PROVIDER_OPTIONS,
  resource: { ...APPOINTMENT_RESOURCE_OPTIONS, allowed_operators: ['is', 'is any of', 'is not'] },
  patient: PATIENT_OPTIONS,
  appointmentTag: {
    ...APPOINTMENT_TAG_OPTIONS,
    allowed_operators: ['is', 'is any of', 'is not'],
    icon: 'tag-mono'
  },
  confirmState: {
    title: 'Confirm state',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: appointmentConfirmState.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'appointment_confirm_state.options',
    icon: 'checkbox-square-mono',
    allowed_operators: ['is', 'is any of', 'is not']
  },
  checkinState: {
    title: 'Checkin state',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: appointmentCheckinState.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'appointment_checkin_state.options',
    icon: 'checkbox-circle-mono',
    allowed_operators: ['is', 'is any of']
  },
  balanceState: {
    title: 'Balance state',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: appointmentBalanceState.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'appointment_balance_state.options',
    icon: 'text-num-mono',
    allowed_operators: ['is', 'is any of', 'is not']
  },
  CCState: {
    title: 'Card on file',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: appointmentCCState.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'appointment_cc_state.options',
    icon: 'card-check-mono',
    allowed_operators: ['is', 'is any of', 'is not']
  },
  clinicalNoteStatus: {
    title: 'Clinical note status',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: clinicalNoteStatus.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'appointment_clinical_note_status.options',
    icon: 'status-mono',
    allowed_operators: ['is', 'is any of', 'is not']
  },
  superbillStatus: {
    title: 'Superbill status',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: superbillStatus.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'appointment_superbill_status.options',
    icon: 'bill-mono',
    allowed_operators: ['is', 'is any of', 'is not']
  },
  claimState: {
    title: 'Claim state',
    multiple: true,
    queryFn: (_, data) => {
      const searchTerm = data.filters.searchTerm || '';
      return {
        options: claimState.filter((status) =>
          status.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      };
    },
    queryKey: 'appointment_claim_state.options',
    icon: 'wallet-mono',
    allowed_operators: ['is', 'is any of', 'is not']
  }
};

export const handleRemind = async ({
  navigate,
  type = '',
  appointments = [],
  setSelectedRows,
  setMultiActionsModal
}) => {
  const params = { appointment_ids: appointments, type };
  await remindAppointmentMultiple(navigate, params);

  setSelectedRows([]);
  setMultiActionsModal((prevState) => ({
    ...prevState,
    appointmentReminder: false
  }));
  showAlert({ message: 'Appointment reminder sent successfully', type: 'success' });
};

export const handleCreationAction = async ({
  navigate,
  type = '',
  appointments = [],
  setSelectedRows,
  setMultiActionsModal
}) => {
  const params = { appointment_ids: appointments, type };
  await creationActionAppointmentMultiple(navigate, params);

  setSelectedRows([]);
  setMultiActionsModal((prevState) => ({
    ...prevState,
    appointmentCreation: false
  }));
  showAlert({ message: 'Appointment creation sent successfully', type: 'success' });
};

export const cancelAppointment = async ({
  setLoading,
  navigate,
  selectedRows,
  queryClient,
  setMultiActionsModal,
  setSelectedRows
}) => {
  setLoading(true);

  const params = { appointments: selectedRows };
  await cancelMultipleAppointments(navigate, params);

  queryClient.invalidateQueries(['appointments']);
  setSelectedRows([]);
  setMultiActionsModal((prevState) => ({
    ...prevState,
    appointmentCancel: false
  }));
  showAlert({ message: 'Appointments are canceled', color: 'success' });
  setLoading(false);
};

export const updateAppointments = async ({
  queryClient,
  navigate,
  setMultiActionsModal,
  status = '',
  appointments = [],
  setSelectedRows
}) => {
  try {
    const params = {
      update_appointment: true,
      appointments: appointments.map((appointment) => ({
        ...appointment?.data,
        status,
        resource_id: appointment?.resources,
        products: appointment?.products || [],
        procedures: appointment?.data?.invoice?.procedures
      }))
    };
    await requestApi({
      url: '/api/appointment/update_multiple',
      navigate,
      params
    });
    queryClient.invalidateQueries(['appointments']);
    showAlert({ message: 'Appointments updated successfully', color: 'success' });
  } catch (error) {
    showAlert({ message: 'Failed to update', color: 'danger' });
  }
  setSelectedRows([]);
  setMultiActionsModal((prevState) => ({
    ...prevState,
    appointmentStatus: false
  }));
};
